import { api, endpoints } from "api/api";
import { AxiosPromise } from "axios";

const move = (courseId: number, registrationId: number, body: { nextCourseId: number }): AxiosPromise => {
  return api.post(endpoints.COURSE_REGISTRATIONS.MOVE(courseId, registrationId), body);
};

const moveEnhanced = (courseId: number, registrationId: number, body: MoveUserEnhancedRequest): AxiosPromise => {
  return api.post(endpoints.COURSE_REGISTRATIONS.MOVE(courseId, registrationId), body);
};

const deleteResource = (
  courseId: number,
  registrationId: number,
  retainedRevenue?: RetainedRevenueChoice,
): Promise<void> => {
  return api.delete(endpoints.COURSE_REGISTRATIONS.DELETE(courseId, registrationId), {
    params: retainedRevenue
      ? { retainedRevenueMode: retainedRevenue.mode, retainedRevenue: retainedRevenue.amount ?? undefined }
      : undefined,
  });
};

const getRevenueSummary = (courseId: number, registrationId: number): Promise<RegistrationRevenueSummaryApi> => {
  return api
    .get<RegistrationRevenueSummaryApi>(endpoints.COURSE_REGISTRATIONS.REVENUE_SUMMARY(courseId, registrationId))
    .then((res) => res.data);
};

const getRemovedRevenue = (courseId: number): Promise<RegistrationRevenueSummaryApi[]> => {
  return api
    .get<RegistrationRevenueSummaryApi[]>(endpoints.COURSE_REGISTRATIONS.REMOVED_REVENUE(courseId))
    .then((res) => res.data);
};

const updateRetainedRevenue = (
  courseId: number,
  registrationId: number,
  retainedRevenue: RetainedRevenueChoice,
): Promise<void> => {
  return api.put(endpoints.COURSE_REGISTRATIONS.RETAINED_REVENUE(courseId, registrationId), {
    mode: retainedRevenue.mode,
    amount: retainedRevenue.amount ?? null,
  });
};

const updateDueDates = (courseId: number): Promise<void> => {
  return api.post(endpoints.COURSE_REGISTRATIONS.UPDATE_DUE_DATES(courseId));
};

const getRegistrationId = (courseId: number, userId: number): Promise<number> => {
  return api.get(endpoints.COURSE_REGISTRATIONS.GET_REGISTRATION_ID(courseId, userId)).then((res) => res.data);
};

const dismissCancellationRequest = (courseId: number, registrationId: number, reason: string): Promise<void> => {
  return api.post(endpoints.COURSE_REGISTRATIONS.DISMISS_CANCELLATION_REQUEST(courseId, registrationId), { reason });
};

export const courseRegistrationApi = {
  move,
  moveEnhanced,
  delete: deleteResource,
  updateDueDates,
  getRegistrationId,
  dismissCancellationRequest,
  getRevenueSummary,
  getRemovedRevenue,
  updateRetainedRevenue,
};

export const RETAINED_REVENUE_MODE = {
  NONE: 1,
  ADVANCE: 2,
  ALL: 3,
  CUSTOM: 4,
} as const;
export type RetainedRevenueMode = (typeof RETAINED_REVENUE_MODE)[keyof typeof RETAINED_REVENUE_MODE];

export interface RetainedRevenueChoice {
  mode: RetainedRevenueMode;
  amount?: number | null;
}

export interface RegistrationRevenueSummaryApi {
  registrationId: number;
  userId: number;
  firstName?: string;
  surname?: string;
  isDeleted: boolean;
  deletedAt?: string | null;
  paidAmount: number;
  advanceAmount: number;
  retainedRevenue: number;
}

export interface TrancheMapping {
  targetCourseTrancheId: number;
  amount: number;
  discountedAmount: number;
  name: string;
  dueDate: Date;
  status: number;
  paymentId: number | null;
}

export interface MoveUserEnhancedRequest {
  targetCourseId: number;
  newTotalPrice: number;
  trancheMappings: TrancheMapping[];
}
